import { h, type View } from '../app/components/dom.js';
import { getMobileBridge } from '../platform/mobile-bridge.js';
import {
  getInstallController,
  isDisplayStandalone,
  type InstallCapabilityController,
  type InstallCapabilitySnapshot,
} from '../app/pwa.js';

type PlatformKind = 'electron' | 'ios' | 'android' | 'unsupported';

function detectPlatform(ua: string): PlatformKind {
  if (typeof window !== 'undefined' && window.asisDesktop) return 'electron';
  const lower = ua.toLowerCase();
  const isIpadOs = lower.includes('ipad') || (lower.includes('macintosh') && typeof navigator !== 'undefined' && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1);
  if (lower.includes('iphone') || lower.includes('ipod') || isIpadOs) return 'ios';
  if (lower.includes('android')) return 'android';
  return 'unsupported';
}

function platformLabel(platform: PlatformKind): string {
  if (platform === 'electron') return 'Aplicación de escritorio';
  if (platform === 'ios') return 'iPhone o iPad';
  if (platform === 'android') return 'Android';
  return 'Navegador';
}

export function createInstallAppCard(opts: { controller?: InstallCapabilityController } = {}): View {
  const native = getMobileBridge();
  if (native) return { el: h('section', { class: 'card install-app' }, [
    h('h2', { class: 'card-title' }, ['ASIS en este dispositivo']),
    h('p', { class: 'card-sub', role: 'status' }, [`Aplicación instalada para ${native.platform === 'ios' ? 'iPhone o iPad' : 'Android'}. Usa tu misma cuenta para compartir dispositivos y conocimiento.`]),
  ]) };
  const controller = opts.controller ?? getInstallController();
  let alive = true;
  const snapshot: InstallCapabilitySnapshot = controller.snapshot();
  let platform: PlatformKind = 'unsupported';
  if (typeof navigator !== 'undefined') platform = detectPlatform(navigator.userAgent);
  const card = h('section', { class: 'card install-app', 'aria-labelledby': 'install-app-title' });
  const title = h('h2', { class: 'card-title', id: 'install-app-title' }, ['Instalar ASIS']);
  const subtitle = h('p', { class: 'card-sub' }, ['Usa la misma cuenta del Mac en este dispositivo. Requiere conexión con el servidor. La voz funciona con la aplicación en primer plano.']);
  const platformTag = h('p', { class: 'card-sub muted' }, [platformLabel(platform)]);
  card.append(title, subtitle, platformTag);

  const stateBox = h('div', { class: 'install-app-state' });
  card.append(stateBox);

  const buttonRow = h('div', { class: 'form-row' });
  const installBtn = h('button', { class: 'btn primary', type: 'button' }, ['Instalar']);
  buttonRow.append(installBtn);
  card.append(buttonRow);

  const instructions = h('ol', { class: 'install-app-instructions', hidden: true });
  card.append(instructions);

  const desktopBox = h('p', { class: 'card-sub', hidden: true, role: 'status' }, ['Estás usando la aplicación de escritorio. No hace falta instalar una web app aquí.']);
  card.append(desktopBox);

  const errorBox = h('p', { class: 'auth-error', role: 'alert', hidden: true });
  card.append(errorBox);

  function renderState(snap: InstallCapabilitySnapshot): void {
    if (!alive) return;
    stateBox.replaceChildren();
    errorBox.hidden = true;
    errorBox.textContent = '';
    instructions.replaceChildren();
    instructions.hidden = true;
    installBtn.hidden = true;
    installBtn.disabled = true;
    desktopBox.hidden = true;

    if (snap.source === 'electron' || platform === 'electron') {
      desktopBox.hidden = false;
      stateBox.replaceChildren(h('p', { class: 'install-app-status', role: 'status', 'aria-live': 'polite' }, ['Contexto de escritorio activo. No hay instrucciones web.']));
      return;
    }
    if (snap.isInstalled) {
      stateBox.replaceChildren(h('p', { class: 'install-app-status', role: 'status', 'aria-live': 'polite' }, ['Aplicación instalada. Se abre como Web App.']));
      return;
    }
    if (snap.hasPrompt && snap.source === 'pending') {
      installBtn.hidden = false;
      installBtn.disabled = false;
      stateBox.replaceChildren(h('p', { class: 'install-app-status', role: 'status', 'aria-live': 'polite' }, ['El navegador permite instalar la versión web en este dispositivo.']));
      return;
    }
    if (snap.hasPrompt && snap.source === 'browser') {
      stateBox.replaceChildren(h('p', { class: 'install-app-status', role: 'status', 'aria-live': 'polite' }, ['Aún no se puede mostrar el diálogo de instalación. Vuelve a abrir esta vista desde un navegador compatible.']));
      showManualInstructions(platform);
      return;
    }
    stateBox.replaceChildren(h('p', { class: 'install-app-status', role: 'status', 'aria-live': 'polite' }, ['Este navegador no muestra el diálogo estándar de instalación. Sigue las instrucciones de tu sistema.']));
    showManualInstructions(platform);
  }

  function showManualInstructions(target: PlatformKind): void {
    instructions.replaceChildren();
    instructions.hidden = false;
    if (target === 'ios') {
      instructions.append(
        h('li', {}, ['Abre esta página en Safari.']),
        h('li', {}, ['Toca Compartir.']),
        h('li', {}, ['Elige Añadir a pantalla de inicio.']),
        h('li', {}, ['Activa Abrir como app web si aparece esa opción.']),
        h('li', {}, ['Confirma Añadir y abre la app desde el icono.']),
      );
      return;
    }
    if (target === 'android') {
      instructions.append(
        h('li', {}, ['Abre el menú del navegador.']),
        h('li', {}, ['Selecciona Instalar aplicación o Añadir a pantalla principal.']),
        h('li', {}, ['Confirma y abre ASIS desde el icono.']),
      );
      return;
    }
    instructions.append(h('li', {}, ['No hay un botón oficial en este navegador; usa el menú del navegador para crear un acceso o abre ASIS en Chrome, Edge o Safari.']));
  }

  installBtn.addEventListener('click', async () => {
    if (!alive || installBtn.disabled) return;
    installBtn.disabled = true;
    stateBox.replaceChildren(h('p', { class: 'install-app-status', role: 'status', 'aria-live': 'polite' }, ['Esperando al navegador…']));
    instructions.replaceChildren();
    instructions.hidden = true;
    let result: Awaited<ReturnType<InstallCapabilityController['requestInstall']>>;
    try { result = await controller.requestInstall(); } catch { result = 'failed'; }
    if (!alive) return;
    const current = controller.snapshot();
    if (current.isInstalled || current.hasPrompt) { renderState(current); return; }
    installBtn.hidden = true;
    installBtn.disabled = true;
    if (result === 'accepted') {
      installBtn.hidden = true;
      stateBox.replaceChildren(h('p', { class: 'install-app-status', role: 'status', 'aria-live': 'polite' }, ['Instalación solicitada. Espera a que el navegador termine o abre ASIS desde el icono.']));
      return;
    }
    if (result === 'dismissed') {
      stateBox.replaceChildren(h('p', { class: 'install-app-status', role: 'status', 'aria-live': 'polite' }, ['Has cancelado el diálogo. Usa el menú del navegador o espera a que vuelva a ofrecer la instalación.']));
      showManualInstructions(platform);
      return;
    }
    if (result === 'failed') {
      errorBox.hidden = false;
      errorBox.textContent = 'No se pudo iniciar la instalación. Sigue las instrucciones manuales de tu sistema.';
      showManualInstructions(platform);
      return;
    }
    stateBox.replaceChildren(h('p', { class: 'install-app-status', role: 'status', 'aria-live': 'polite' }, ['El navegador aún no ha preparado el diálogo. Vuelve a abrir esta vista.']));
    showManualInstructions(platform);
  });

  if (platform === 'ios' && isDisplayStandalone()) {
    renderState({ hasPrompt: false, isInstalled: true, source: 'installed' });
  } else {
    renderState(snapshot);
  }

  const unsubscribe = controller.subscribe(renderState);
  return {
    el: card,
    destroy() {
      alive = false;
      unsubscribe();
    },
  };
}
